document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const initStickyHeader = (header) => {
  if (!header || header.dataset.stickyHeaderInitialized === 'true') return;
  const logo = header.querySelector('.site-header__logo');

  const update = () => {
    if (!header.hasAttribute('data-overlay-header')) return;
    const hasStartedScrolling = window.scrollY > 4;
    header.classList.toggle('is-scrolled', hasStartedScrolling);

    if (logo) {
      const nextLogo = hasStartedScrolling ? header.dataset.logoDark : header.dataset.logoLight;
      if (nextLogo && logo.getAttribute('src') !== nextLogo) logo.setAttribute('src', nextLogo);
    }
  };

  let frameRequested = false;
  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(() => {
      frameRequested = false;
      update();
    });
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  header.dataset.stickyHeaderInitialized = 'true';
  update();
};

document.querySelectorAll('[data-site-header]').forEach(initStickyHeader);
document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-site-header]').forEach(initStickyHeader));

const setCommerceMessage = (node, message = '', isError = false) => {
  if (!node) return;
  node.textContent = message;
  node.hidden = !message;
  node.classList.toggle('is-error', Boolean(message) && isError);
  node.setAttribute('role', isError ? 'alert' : 'status');
};

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-localization-select]')) event.target.form.submit();
});

const closeMobileMenu = (menu, returnFocus = true) => {
  if (!menu?.open) return;
  menu.open = false;
  menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  if (!document.querySelector('[data-mobile-menu][open]')) document.body.classList.remove('mobile-menu-open');
  if (returnFocus) menu.querySelector('summary')?.focus();
};

const closeAllMobileMenus = () => {
  document.querySelectorAll('[data-mobile-menu][open]').forEach((menu) => closeMobileMenu(menu, false));
};

const initMobileMenu = (menu) => {
  if (!menu || menu.dataset.mobileMenuInitialized === 'true') return;
  const trigger = menu.querySelector('summary');
  const panel = menu.querySelector('[data-mobile-menu-panel]');

  menu.addEventListener('toggle', () => {
    trigger?.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
    trigger?.setAttribute('aria-label', menu.open ? trigger.dataset.closeLabel : trigger.dataset.openLabel);
    if (menu.open) {
      document.querySelectorAll('[data-mobile-menu][open]').forEach((other) => { if (other !== menu) closeMobileMenu(other, false); });
      document.body.classList.add('mobile-menu-open');
      window.requestAnimationFrame(() => menu.querySelector('[data-mobile-menu-close]:not([tabindex="-1"])')?.focus());
    } else if (!document.querySelector('[data-mobile-menu][open]')) {
      document.body.classList.remove('mobile-menu-open');
    }
  });

  menu.querySelectorAll('[data-mobile-menu-close]').forEach((button) => button.addEventListener('click', () => closeMobileMenu(menu)));
  menu.querySelectorAll('[data-mobile-submenu-toggle]').forEach((button) => button.addEventListener('click', () => {
    const submenu = document.getElementById(button.getAttribute('aria-controls'));
    if (!submenu) return;
    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    submenu.hidden = !shouldOpen;
  }));

  menu.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !menu.open || !panel) return;
    const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((item) => !item.hidden && item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  menu.dataset.mobileMenuInitialized = 'true';
};

document.querySelectorAll('[data-mobile-menu]').forEach(initMobileMenu);
document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-mobile-menu]').forEach(initMobileMenu));
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-mobile-menu][open]').forEach((menu) => closeMobileMenu(menu));
});

let cartDrawerReturnFocus = null;

const getCartDrawer = () => document.querySelector('[data-cart-drawer]');

const closeCartDrawer = (returnFocus = true) => {
  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-open')) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cart-drawer-open');
  if (returnFocus && cartDrawerReturnFocus?.isConnected) cartDrawerReturnFocus.focus();
};

const openCartDrawer = (trigger = document.activeElement) => {
  const drawer = getCartDrawer();
  if (!drawer) return;
  closeSearchDrawer(false);
  closeAllMobileMenus();
  cartDrawerReturnFocus = trigger;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-drawer-open');
  window.requestAnimationFrame(() => drawer.querySelector('[data-cart-drawer-close]')?.focus());
};

let searchDrawerReturnFocus = null;
let searchDrawerRequest;
let searchDrawerTimer;

const getSearchDrawer = () => document.querySelector('[data-search-drawer]');

const closeSearchDrawer = (returnFocus = true) => {
  const drawer = getSearchDrawer();
  if (!drawer?.classList.contains('is-open')) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('search-drawer-open');
  if (returnFocus && searchDrawerReturnFocus?.isConnected) searchDrawerReturnFocus.focus();
};

const openSearchDrawer = (trigger = document.activeElement) => {
  const drawer = getSearchDrawer();
  if (!drawer) return;
  closeCartDrawer(false);
  closeAllMobileMenus();
  searchDrawerReturnFocus = trigger;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('search-drawer-open');
  window.requestAnimationFrame(() => drawer.querySelector('[data-search-drawer-input]')?.focus());
};

const loadPredictiveSearch = async (drawer, term) => {
  const results = drawer.querySelector('[data-search-drawer-results]');
  const input = drawer.querySelector('[data-search-drawer-input]');
  if (!results || !input) return;
  searchDrawerRequest?.abort();
  const request = new AbortController();
  searchDrawerRequest = request;
  results.setAttribute('aria-busy', 'true');
  try {
    const params = new URLSearchParams({ q: term, 'resources[type]': 'product,article,page', 'resources[limit]': '8', section_id: 'predictive-search' });
    const response = await fetch(`${drawer.dataset.predictiveSearchUrl}?${params}`, { signal: request.signal });
    if (!response.ok) throw new Error('Predictive search failed');
    const html = await response.text();
    const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-search-content]');
    results.innerHTML = content?.outerHTML || '';
    input.setAttribute('aria-expanded', content ? 'true' : 'false');
  } catch (error) {
    if (error.name !== 'AbortError') {
      input.setAttribute('aria-expanded', 'false');
      results.innerHTML = '';
      const message = document.createElement('p');
      message.className = 'search-drawer__hint commerce-message is-error';
      message.setAttribute('role', 'alert');
      message.textContent = drawer.dataset.errorMessage;
      results.append(message);
    }
  } finally {
    if (searchDrawerRequest === request) results.removeAttribute('aria-busy');
  }
};

document.addEventListener('input', (event) => {
  const input = event.target.closest('[data-search-drawer-input]');
  if (!input) return;
  const drawer = input.closest('[data-search-drawer]');
  const term = input.value.trim();
  window.clearTimeout(searchDrawerTimer);
  if (term.length < 2) {
    input.setAttribute('aria-expanded', 'false');
    drawer.querySelector('[data-search-drawer-results]').innerHTML = '';
    return;
  }
  searchDrawerTimer = window.setTimeout(() => loadPredictiveSearch(drawer, term), 220);
});

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-search-drawer-open]');
  if (opener && getSearchDrawer()) { event.preventDefault(); openSearchDrawer(opener); return; }
  if (event.target.closest('[data-search-drawer-close]')) closeSearchDrawer();
});

const updateCartCounts = (count) => {
  document.querySelectorAll('.header-cart__count').forEach((node) => { node.textContent = count; });
  document.querySelectorAll('[data-cart-count-text]').forEach((node) => { node.textContent = `(${count})`; });
};

let cartDrawerNoticeTimer;

const refreshCartDrawer = async () => {
  const response = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`);
  if (!response.ok) throw new Error('Cart drawer could not be refreshed');
  const html = await response.text();
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
  const current = getCartDrawer();
  if (!next || !current) return;
  current.replaceWith(next);
};

const setCartDrawerBusy = (drawer, busy, activeItem = null) => {
  if (!drawer) return;
  drawer.querySelector('[data-cart-drawer-body]')?.setAttribute('aria-busy', busy ? 'true' : 'false');
  drawer.querySelectorAll('.cart-drawer-item').forEach((item) => {
    const isActive = busy && item === activeItem;
    item.classList.toggle('is-updating', isActive);
    if (isActive) item.setAttribute('aria-busy', 'true');
    else item.removeAttribute('aria-busy');
  });
  drawer.querySelectorAll('[data-cart-drawer-quantity-change], [data-cart-drawer-quantity], [data-cart-drawer-remove]').forEach((control) => {
    control.disabled = busy;
  });
};

const showCartDrawerError = (status, error, item) => {
  const isInventoryLimit = error?.status === 422;
  const message = isInventoryLimit ? item?.dataset.inventoryErrorMessage : item?.dataset.errorMessage;
  item?.classList.add(isInventoryLimit ? 'has-notice' : 'has-error');
  setCommerceMessage(status, message, !isInventoryLimit);

  if (isInventoryLimit) {
    window.clearTimeout(cartDrawerNoticeTimer);
    cartDrawerNoticeTimer = window.setTimeout(() => {
      if (item?.isConnected) item.classList.remove('has-notice');
      if (status?.isConnected) setCommerceMessage(status);
    }, 4200);
  }
};

const clearCartDrawerError = (drawer, status) => {
  window.clearTimeout(cartDrawerNoticeTimer);
  drawer?.querySelectorAll('.cart-drawer-item.has-error, .cart-drawer-item.has-notice').forEach((item) => item.classList.remove('has-error', 'has-notice'));
  setCommerceMessage(status);
};

const changeCartDrawerLine = async (line, quantity) => {
  const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line, quantity })
  });
  if (!response.ok) {
    const error = new Error('Cart update failed');
    error.status = response.status;
    throw error;
  }
  const cart = await response.json();
  await refreshCartDrawer();
  updateCartCounts(cart.item_count);
  openCartDrawer(cartDrawerReturnFocus);
};

document.addEventListener('click', async (event) => {
  const opener = event.target.closest('[data-cart-drawer-open]');
  if (opener && getCartDrawer()) {
    event.preventDefault();
    openCartDrawer(opener);
    return;
  }

  if (event.target.closest('[data-cart-drawer-close]')) {
    closeCartDrawer();
    return;
  }

  const quantityButton = event.target.closest('[data-cart-drawer-quantity-change]');
  const remove = event.target.closest('[data-cart-drawer-remove]');
  if (!quantityButton && !remove) return;
  const control = quantityButton || remove;
  const item = control.closest('.cart-drawer-item');
  const quantityInput = item?.querySelector('[data-cart-drawer-quantity]');
  const quantity = remove ? 0 : Math.max(0, Number(quantityInput?.value || 0) + Number(quantityButton.dataset.cartDrawerQuantityChange));
  const drawer = control.closest('[data-cart-drawer]');
  const status = drawer?.querySelector('[data-cart-drawer-status]');
  clearCartDrawerError(drawer, status);
  setCartDrawerBusy(drawer, true, item);
  try {
    await changeCartDrawerLine(Number(control.dataset.line), quantity);
  } catch (error) {
    setCartDrawerBusy(drawer, false);
    showCartDrawerError(status, error, item);
  }
});

document.addEventListener('change', async (event) => {
  const input = event.target.closest('[data-cart-drawer-quantity]');
  if (!input) return;
  const drawer = input.closest('[data-cart-drawer]');
  const status = drawer?.querySelector('[data-cart-drawer-status]');
  const item = input.closest('.cart-drawer-item');
  clearCartDrawerError(drawer, status);
  setCartDrawerBusy(drawer, true, item);
  try {
    await changeCartDrawerLine(Number(input.dataset.line), Math.max(0, Number(input.value || 0)));
  } catch (error) {
    setCartDrawerBusy(drawer, false);
    showCartDrawerError(status, error, item);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-quick-add-form]');
  if (!form || !getCartDrawer()) return;
  event.preventDefault();
  const button = form.querySelector('[data-quick-add-button]');
  const text = form.querySelector('[data-quick-add-text]');
  const status = form.querySelector('[data-quick-add-status]');
  if (!button || button.disabled) return;
  button.disabled = true;
  button.classList.remove('is-added');
  setCommerceMessage(status);
  if (text) text.textContent = button.dataset.loadingLabel;
  let itemAdded = false;
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
    if (!response.ok) throw new Error('Quick add failed');
    itemAdded = true;
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!cartResponse.ok) throw new Error('Cart could not be read');
    const cart = await cartResponse.json();
    await refreshCartDrawer();
    updateCartCounts(cart.item_count);
    button.classList.add('is-added');
    if (text) text.textContent = button.dataset.addedLabel;
    setCommerceMessage(status, button.dataset.addedLabel);
    openCartDrawer(button);
  } catch (error) {
    setCommerceMessage(status, itemAdded ? status?.dataset.refreshErrorMessage : status?.dataset.errorMessage, true);
  } finally {
    window.setTimeout(() => {
      button.disabled = false;
      button.classList.remove('is-added');
      if (text) text.textContent = button.dataset.idleLabel;
      if (!status?.classList.contains('is-error')) setCommerceMessage(status);
    }, 1200);
  }
});

document.addEventListener('keydown', (event) => {
  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-open')) return;
  if (event.key === 'Escape') { closeCartDrawer(); return; }
  if (event.key !== 'Tab') return;
  const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((item) => item.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
});

document.addEventListener('keydown', (event) => {
  const drawer = getSearchDrawer();
  if (!drawer?.classList.contains('is-open')) return;
  if (event.key === 'Escape') { closeSearchDrawer(); return; }
  if (event.key !== 'Tab') return;
  const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((item) => item.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
});

const desktopHeader = window.matchMedia('(min-width: 64.01rem)');
desktopHeader.addEventListener('change', (event) => {
  if (event.matches) document.querySelectorAll('[data-mobile-menu][open]').forEach((menu) => closeMobileMenu(menu, false));
});

const initProductSection = (section) => {
  if (!section || section.dataset.productInitialized === 'true') return;
  const variantsNode = section.querySelector('[data-product-variants]');
  if (!variantsNode) return;

  let variants;
  try { variants = JSON.parse(variantsNode.textContent); } catch (error) { return; }

  const variantInputs = section.querySelectorAll('[data-variant-input]');
  const price = section.querySelector('[data-product-price]');
  const availability = section.querySelector('[data-product-availability]');
  const availabilityText = section.querySelector('[data-availability-text]');
  const submit = section.querySelector('[data-add-to-cart]');
  const submitText = section.querySelector('[data-add-to-cart-text]');
  const formStatus = section.querySelector('[data-product-form-status]');
  const details = document.querySelector('[data-product-details]');
  const translations = {
    add: submit?.dataset.addLabel || '',
    adding: submit?.dataset.loadingLabel || '',
    soldOut: submit?.dataset.soldOutLabel || ''
  };

  const showMedia = (mediaId) => {
    if (!mediaId) return;
    const media = section.querySelector(`[data-media-id="${mediaId}"]`);
    if (!media) return;
    media.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  };

  const updateVariant = (id) => {
    const variant = variants.find((item) => String(item.id) === String(id));
    if (!variant) return;

    section.querySelectorAll('.variant-card').forEach((card) => card.classList.toggle('is-selected', card.querySelector('input')?.checked));
    if (price) price.innerHTML = `${variant.compareAtPrice ? `<s>${variant.compareAtPrice}</s>` : ''}<span>${variant.price}</span>`;
    if (availability) availability.classList.toggle('is-unavailable', !variant.available);
    if (availabilityText) availabilityText.textContent = variant.available ? availability.dataset.inStock : availability.dataset.soldOut;
    if (submit) submit.disabled = !variant.available;
    if (submitText) submitText.textContent = variant.available ? translations.add : translations.soldOut;
    if (details) {
      const sku = details.querySelector('[data-product-sku]');
      const weight = details.querySelector('[data-product-weight]');
      const burnTime = details.querySelector('[data-product-burn-time]');
      const burnTimeRow = details.querySelector('[data-burn-time-row]');
      if (sku && variant.sku) sku.textContent = variant.sku;
      if (weight && variant.weight) weight.textContent = variant.weight;
      if (burnTime) burnTime.textContent = variant.burnTime || '';
      if (burnTimeRow) burnTimeRow.hidden = !variant.burnTime;
    }
    showMedia(variant.featuredMediaId);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  variantInputs.forEach((input) => input.addEventListener('change', () => updateVariant(input.value)));
  section.querySelectorAll('[data-quantity-change]').forEach((button) => button.addEventListener('click', () => {
    const input = section.querySelector('[data-quantity-input]');
    if (!input) return;
    input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange));
  }));
  section.querySelectorAll('[data-media-target]').forEach((button) => button.addEventListener('click', () => {
    const target = document.getElementById(button.dataset.mediaTarget);
    target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    section.querySelectorAll('[data-media-target]').forEach((item) => { item.classList.toggle('is-active', item === button); item.setAttribute('aria-current', item === button ? 'true' : 'false'); });
  }));
  const productForm = section.querySelector('[data-product-form]');
  productForm?.addEventListener('submit', async (event) => {
    if (!getCartDrawer()) return;
    event.preventDefault();
    if (submit) submit.disabled = true;
    if (submitText) submitText.textContent = translations.adding;
    setCommerceMessage(formStatus);
    let itemAdded = false;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(productForm) });
      if (!response.ok) throw new Error('Add to cart failed');
      itemAdded = true;
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) throw new Error('Cart could not be read');
      const cart = await cartResponse.json();
      await refreshCartDrawer();
      updateCartCounts(cart.item_count);
      openCartDrawer(submit);
    } catch (error) {
      setCommerceMessage(formStatus, itemAdded ? formStatus?.dataset.refreshErrorMessage : formStatus?.dataset.errorMessage, true);
    } finally {
      if (submit) submit.disabled = false;
      if (submitText) submitText.textContent = translations.add;
    }
  });
  section.dataset.productInitialized = 'true';
};

document.querySelectorAll('[data-product-section]').forEach(initProductSection);
document.addEventListener('shopify:section:load', (event) => initProductSection(event.target.querySelector('[data-product-section]')));

let mainCartNoticeTimer;

const setMainCartBusy = (section, activeItem, busy) => {
  if (!section) return;
  section.setAttribute('aria-busy', busy ? 'true' : 'false');
  section.querySelectorAll('[data-cart-quantity-change], [data-cart-quantity-input], [data-cart-page-remove], [name="checkout"]').forEach((control) => {
    if ('disabled' in control) control.disabled = busy;
    else control.setAttribute('aria-disabled', busy ? 'true' : 'false');
  });
  section.querySelectorAll('[data-cart-page-item]').forEach((item) => {
    item.classList.toggle('is-updating', busy && item === activeItem);
  });
};

const clearMainCartFeedback = (section, status) => {
  window.clearTimeout(mainCartNoticeTimer);
  section?.querySelectorAll('.cart-item.has-error, .cart-item.has-notice').forEach((item) => item.classList.remove('has-error', 'has-notice'));
  setCommerceMessage(status);
};

const showMainCartFeedback = (status, error, item) => {
  const isInventoryLimit = error?.status === 422;
  const message = isInventoryLimit ? item?.dataset.inventoryErrorMessage : item?.dataset.errorMessage;
  item?.classList.add(isInventoryLimit ? 'has-notice' : 'has-error');
  setCommerceMessage(status, message, !isInventoryLimit);

  if (isInventoryLimit) {
    mainCartNoticeTimer = window.setTimeout(() => {
      if (item?.isConnected) item.classList.remove('has-notice');
      if (status?.isConnected) setCommerceMessage(status);
    }, 4200);
  }
};

const refreshMainCart = async (section) => {
  const response = await fetch(`${window.Shopify.routes.root}cart?section_id=${encodeURIComponent(section.dataset.sectionId)}`);
  if (!response.ok) throw new Error('Cart page could not be refreshed');
  const html = await response.text();
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-main-cart]');
  if (!next) throw new Error('Cart page section was not returned');
  section.replaceWith(next);
};

const changeMainCartLine = async (section, item, quantity) => {
  const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line: Number(item.dataset.line), quantity })
  });
  if (!response.ok) {
    const error = new Error('Cart update failed');
    error.status = response.status;
    throw error;
  }
  const cart = await response.json();
  try {
    await refreshMainCart(section);
  } catch (error) {
    window.location.reload();
    return;
  }
  updateCartCounts(cart.item_count);
  refreshCartDrawer().catch(() => {});
};

const updateMainCartItem = async (section, item, input, quantity) => {
  const status = section.querySelector('[data-main-cart-status]');
  clearMainCartFeedback(section, status);
  setMainCartBusy(section, item, true);
  try {
    await changeMainCartLine(section, item, quantity);
  } catch (error) {
    setMainCartBusy(section, item, false);
    input.value = input.dataset.currentQuantity;
    showMainCartFeedback(status, error, item);
  }
};

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-quantity-change]');
  const remove = event.target.closest('[data-cart-page-remove]');
  if (!button && !remove) return;
  if (button?.disabled || remove?.getAttribute('aria-disabled') === 'true') return;
  const item = (button || remove).closest('[data-cart-page-item]');
  const section = item?.closest('[data-main-cart]');
  const input = item?.querySelector('[data-cart-quantity-input]');
  if (!section || !item || !input) return;
  event.preventDefault();
  const quantity = remove ? 0 : Math.max(0, Number(input.dataset.currentQuantity || 0) + Number(button.dataset.cartQuantityChange));
  await updateMainCartItem(section, item, input, quantity);
});

const filterBreakpoint = window.matchMedia('(max-width: 40rem)');

const syncResponsiveFilters = (scope = document) => {
  scope.querySelectorAll('[data-responsive-filters]').forEach((filters) => {
    filters.open = !filterBreakpoint.matches;
  });
};

syncResponsiveFilters();
if (filterBreakpoint.addEventListener) {
  filterBreakpoint.addEventListener('change', () => syncResponsiveFilters());
} else {
  filterBreakpoint.addListener(() => syncResponsiveFilters());
}
document.addEventListener('shopify:section:load', (event) => syncResponsiveFilters(event.target));

const closeCollectionDialog = (dialog) => {
  if (!dialog?.open || dialog.classList.contains('is-closing')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    dialog.close();
    return;
  }
  dialog.classList.add('is-closing');
  window.setTimeout(() => {
    dialog.close();
    dialog.classList.remove('is-closing');
  }, 170);
};

document.addEventListener('click', (event) => {
  const openButton = event.target.closest('[data-collection-dialog-open]');
  const closeButton = event.target.closest('[data-collection-dialog-close]');

  if (openButton) {
    const dialog = document.getElementById(openButton.dataset.collectionDialogOpen);
    if (dialog?.showModal) dialog.showModal();
  }

  if (closeButton) closeCollectionDialog(closeButton.closest('[data-collection-dialog]'));

  if (event.target.matches?.('[data-collection-dialog]')) closeCollectionDialog(event.target);
});

document.addEventListener('cancel', (event) => {
  if (!event.target.matches?.('[data-collection-dialog]')) return;
  event.preventDefault();
  closeCollectionDialog(event.target);
}, true);

document.addEventListener('change', async (event) => {
  const input = event.target.closest('[data-main-cart] [data-cart-quantity-input]');
  if (!input) return;
  const item = input.closest('[data-cart-page-item]');
  const section = item?.closest('[data-main-cart]');
  if (!section || !item) return;
  const quantity = Math.max(0, Number(input.value || 0));
  await updateMainCartItem(section, item, input, quantity);
});
